import React, { useEffect, useState } from 'react'
import {
  Box, Button, Card, CardContent, CardHeader, Chip, Divider, Grid, IconButton,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import Alert from '@material-ui/lab/Alert'
import api from '../../api/client'
import { toFarsiDigits, toEnglishDigits } from '../../assets/utils'

const useStyles = makeStyles((theme) => ({
  root: { background: '#f1f1f1', flexGrow: 1 },
  headCell: {
    background: '#1a237e', color: '#fff', fontWeight: 700, fontSize: 12.5,
    position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap',
  },
  row: { '&:nth-of-type(odd)': { background: '#f7f8fc' } },
  formBox: {
    background: '#f7f8fc', border: '1px solid #e3e6f2', borderRadius: 12,
    padding: theme.spacing(2), marginBottom: theme.spacing(2),
  },
  hint: { fontSize: 12.5, color: '#5a6b8c', marginBottom: theme.spacing(1.5), lineHeight: 2 },
}))

const formatDate = (iso) => {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleDateString('fa-IR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return '-'
  }
}

/**
 * «مدیران سازمان» — تعریف/مدیریت توسطِ کاربرِ سازمان.
 * نامِ کاربری = کدِ ملیِ مدیر · رمز را سازمان تعیین می‌کند (مدیر خودش هم
 * می‌تواند آن را از صفحهٔ «مدیریت کاربر» عوض کند).
 */
const DepartmentManagers = () => {
  const classes = useStyles()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(false)

  // فرمِ ساخت
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [saving, setSaving] = useState(false)

  // فرمِ تغییرِ رمز
  const [editFor, setEditFor] = useState(null)
  const [editPassword, setEditPassword] = useState('')
  const [editMobile, setEditMobile] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get('users/department-managers/')
      setRows(Array.isArray(res.data) ? res.data : (res.data?.results || []))
    } catch {
      setErr('خطا در دریافت مدیران سازمان')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const resetForm = () => {
    setUsername(''); setPassword(''); setPassword2(''); setName(''); setMobile('')
  }

  const handleCreate = async () => {
    setErr(''); setMsg('')
    if (password !== password2) {
      setErr('کلمه عبور و تکرار آن مطابقت ندارد.')
      return
    }
    if (!/^\d{10}$/.test(username.trim())) {
      setErr('کد ملی باید ۱۰ رقم باشد.')
      return
    }
    if (!/^\d{4,}$/.test(password)) {
      setErr('کلمه عبور باید حداقل ۴ رقم باشد.')
      return
    }
    if (mobile.trim() && !/^09\d{9}$/.test(toEnglishDigits(mobile.trim()).replace(/\s/g, ''))) {
      setErr('شمارهٔ همراه باید ۱۱ رقم و با ۰۹ آغاز شود.')
      return
    }
    setSaving(true)
    try {
      await api.post('users/department-managers/', {
        username: username.trim(), password, name: name.trim(), mobile: mobile.trim(),
      })
      setMsg('مدیر سازمان با موفقیت ساخته شد.')
      resetForm()
      setOpen(false)
      load()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'خطا در ساخت مدیر سازمان')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (row) => {
    setErr(''); setMsg('')
    // رمز و شماره تلفن هر دو اختیاری‌اند؛ هر کدام پر شد، همان به‌روز می‌شود
    if (editPassword && !/^\d{4,}$/.test(editPassword)) {
      setErr('کلمه عبور باید حداقل ۴ رقم باشد.')
      return
    }
    if (!editPassword && !editMobile.trim()) {
      setErr('رمز یا شماره تلفن را وارد کنید.')
      return
    }
    if (editMobile.trim() && !/^09\d{9}$/.test(toEnglishDigits(editMobile.trim()).replace(/\s/g, ''))) {
      setErr('شمارهٔ همراه باید ۱۱ رقم و با ۰۹ آغاز شود.')
      return
    }
    try {
      await api.put('users/department-managers/', {
        id: row.id, password: editPassword || null, mobile: editMobile.trim(),
      })
      setMsg('اطلاعات مدیر به‌روز شد.')
      setEditFor(null)
      setEditPassword(''); setEditMobile('')
      load()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'خطا در تغییر اطلاعات مدیر')
    }
  }

  const handleDelete = async (row) => {
    setErr(''); setMsg('')
    if (!window.confirm(`مدیر «${row.name || row.username}» حذف شود؟`)) return
    try {
      await api.delete('users/department-managers/', { data: { id: row.id } })
      setMsg('مدیر سازمان حذف شد.')
      load()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'خطا در حذف مدیر سازمان')
    }
  }

  return (
    <Card elevation={5} className={classes.root} data-tour="dept-managers">
      <CardHeader
        title="مدیران سازمان"
        subheader="حساب‌هایی با دسترسی کاملِ سازمان، بجزِ آپلودِ اطلاعات"
        action={
          <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />}
            onClick={() => { resetForm(); setOpen(!open); setErr('') }}>
            افزودن مدیر
          </Button>
        }
      />
      <Divider />
      <CardContent>
        {open && (
          <Box className={classes.formBox}>
            <Typography className={classes.hint}>
              نامِ کاربری = <b>کد ملیِ مدیر</b> · کلمه عبور را خودِ سازمان تعیین می‌کند
              (مدیر می‌تواند بعداً آن را از صفحهٔ «مدیریت کاربر» تغییر دهد).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" variant="outlined" label="کد ملی (نام کاربری)"
                  value={username} onChange={(e) => setUsername(e.target.value)}
                  name="mgr-national-code" autoComplete="off" inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" variant="outlined" label="نام و نام خانوادگی"
                  value={name} onChange={(e) => setName(e.target.value)}
                  name="mgr-full-name" autoComplete="off" inputProps={{ autoComplete: 'off' }} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" variant="outlined" label="شماره تلفن همراه"
                  value={mobile} onChange={(e) => setMobile(e.target.value)}
                  placeholder="09123456789" dir="ltr"
                  name="mgr-mobile" autoComplete="off" inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" variant="outlined" label="کلمه عبور" type="password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  name="mgr-password" autoComplete="new-password" inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" variant="outlined" label="تکرار کلمه عبور" type="password"
                  value={password2} onChange={(e) => setPassword2(e.target.value)}
                  name="mgr-password2" autoComplete="new-password" inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} />
              </Grid>
              <Grid item xs={12} md={2} style={{ display: 'flex', alignItems: 'center' }}>
                <Button variant="contained" color="primary" onClick={handleCreate} disabled={saving}
                  style={{ fontWeight: 700, width: '100%' }}>
                  {saving ? 'در حال ذخیره...' : 'ساخت مدیر'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {err && <Alert severity="error" style={{ marginBottom: 12 }}>{err}</Alert>}
        {msg && <Alert severity="success" style={{ marginBottom: 12 }}>{msg}</Alert>}

        <TableContainer style={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} align="center">ردیف</TableCell>
                <TableCell className={classes.headCell} align="center">نام کاربری (کد ملی)</TableCell>
                <TableCell className={classes.headCell} align="center">نام</TableCell>
                <TableCell className={classes.headCell} align="center">شماره تلفن</TableCell>
                <TableCell className={classes.headCell} align="center">تاریخ ساخت</TableCell>
                <TableCell className={classes.headCell} align="center">آخرین ورود</TableCell>
                <TableCell className={classes.headCell} align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id} className={classes.row}>
                  <TableCell align="center">{toFarsiDigits(i + 1)}</TableCell>
                  <TableCell align="center" dir="ltr">{toFarsiDigits(r.username)}</TableCell>
                  <TableCell align="center">{toFarsiDigits(r.name)}</TableCell>
                  <TableCell align="center" dir="ltr">
                    {r.mobile ? toFarsiDigits('0' + String(r.mobile).replace(/^0/, '')) : '—'}
                  </TableCell>
                  <TableCell align="center">{formatDate(r.created_at)}</TableCell>
                  <TableCell align="center">{formatDate(r.last_login)}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                      <Button size="small" variant="outlined" color="primary"
                        startIcon={<EditIcon style={{ fontSize: 16 }} />}
                        onClick={() => {
                          setEditFor(editFor === r.id ? null : r.id)
                          setEditPassword('')
                          setEditMobile(r.mobile ? '0' + String(r.mobile).replace(/^0/, '') : '')
                          setErr('')
                        }}>
                        ویرایش
                      </Button>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r)}>
                        <DeleteIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    {editFor === r.id && (
                      <Box display="flex" style={{ gap: 6, marginTop: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <TextField size="small" variant="outlined" type="password" placeholder="رمز جدید"
                          value={editPassword} onChange={(e) => setEditPassword(e.target.value)}
                          name="mgr-edit-password" autoComplete="new-password"
                          inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} style={{ width: 140 }} />
                        <TextField size="small" variant="outlined" placeholder="شماره تلفن"
                          value={editMobile} onChange={(e) => setEditMobile(e.target.value)}
                          name="mgr-edit-mobile" autoComplete="off"
                          inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }} style={{ width: 150 }} dir="ltr" />
                        <Button size="small" variant="contained" color="primary"
                          onClick={() => handleChangePassword(r)}>ثبت</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !rows.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography style={{ padding: 18, color: '#8a92b2' }}>
                      هنوز مدیری برای این سازمان تعریف نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

export default DepartmentManagers
